import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Sparkles,
  History,
  CheckCircle2,
  Trash2,
  Zap,
  Save,
  Wrench,
  Shield,
  Moon
} from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import CustomDropdown from "../components/CustomDropdown";
import { useToast } from "../context/ToastContext";
import SimpleMdeEditor from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";

const formatDate = (dateVal) => {
  if (!dateVal) return "Just now";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "Just now";
  try {
    return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
  } catch (e) {
    return d.toLocaleDateString();
  }
};

const CHANNELS = [
  { id: "widget", name: "Chat Widget" },
  { id: "email", name: "Email" },
  { id: "freshchat", name: "Freshchat" },
  { id: "freshdesk", name: "Freshdesk" },
  { id: "slack", name: "Slack" },
  { id: "telegram", name: "Telegram" },
  { id: "webchat", name: "Web Chat" },
  { id: "webhook", name: "Webhook" },
  { id: "whatsapp", name: "WhatsApp" },
  { id: "zendesk", name: "Zendesk" }
];

const SPECIAL_AT_ITEMS = [
  { id: '__request_human__', name: 'request_human_assistant', label: 'Request Human Assistant', description: 'Escalate conversation to a live human agent', type: 'special' }
];

const TAB_ORDER = ["current", "history"];

const PromptVersioning = ({ activeAiAgentId }) => {
  const [selectedChannel, setSelectedChannel] = useState(CHANNELS[0].id);
  const [prompts, setPrompts] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [versionName, setVersionName] = useState("");
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // 'current' or 'history'
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  const [isFullScreen] = useState(false);

  // Guardrails state
  const [guardrailsEnabled, setGuardrailsEnabled] = useState(false);
  const [guardrailsContent, setGuardrailsContent] = useState("");
  const [savingGuardrails, setSavingGuardrails] = useState(false);

  // Widget config state for checking AI After-Hours Support feature toggle
  const [widgetConfig, setWidgetConfig] = useState(null);

  // After-hours instructions state
  const [afterHoursEnabled, setAfterHoursEnabled] = useState(false);
  const [afterHoursInstruction, setAfterHoursInstruction] = useState("");
  // 'normal' or 'after_hours' prompt mode in the editor
  const [instructionMode, setInstructionMode] = useState("normal");

  const handleSaveGuardrails = async (overrideEnabled = null) => {
    if (!activeAiAgentId) return;
    const isEnabled = typeof overrideEnabled === "boolean" ? overrideEnabled : guardrailsEnabled;
    setSavingGuardrails(true);
    try {
      const response = await api.updateGuardrails(
        selectedChannel,
        isEnabled,
        guardrailsContent
      );
      if (response && response.message) {
        showToast(typeof overrideEnabled === "boolean" ? `Guardrails ${overrideEnabled ? "enabled" : "disabled"}` : "Guardrails saved successfully", "success");
        await loadPrompts();
      }
    } catch (error) {
      console.error("Error saving guardrails:", error);
      showToast("Failed to save guardrails", "error");
    } finally {
      setSavingGuardrails(false);
    }
  };

  const loadPrompts = React.useCallback(async () => {
    try {
      const response = await api.getInstructionVersions(selectedChannel);
      const versions = response.versions || [];
      setPrompts(versions);

      const active = versions.find(p => p.is_active);
      if (active) {
        if (activeTab === 'current') {
          setCurrentPrompt(active.content);
        }
        setGuardrailsEnabled(active.guardrails_enabled || false);
        setGuardrailsContent(active.guardrails_content || "");
        setAfterHoursEnabled(active.after_hours_enabled || false);
        setAfterHoursInstruction(active.after_hours_instruction || "");
      } else if (!active && activeTab === 'current') {
        setCurrentPrompt("");
        setGuardrailsEnabled(false);
        setGuardrailsContent("");
        setAfterHoursEnabled(false);
        setAfterHoursInstruction("");
      }
    } catch (error) {
      console.error("Error loading prompts:", error);
    }
  }, [activeTab, setCurrentPrompt, selectedChannel]);

  const mdeOptions = React.useMemo(() => ({
    autofocus: false,
    spellChecker: false,
    placeholder: instructionMode === "after_hours"
      ? "e.g. Inform customers that our human team is offline. Gather their email address, order ID, and inquiry details, and let them know we will prioritize their ticket at 9 AM tomorrow..."
      : "e.g. You are a helpful assistant for EasyChat...",
    status: false,
    toolbar: [
      "bold", "italic", "strikethrough", "|",
      "heading-1", "heading-2", "heading-3", "|",
      "unordered-list", "ordered-list", "|",
      "code", "quote", "horizontal-rule", "table"
    ]
  }), [instructionMode]);

  // Suggestions state
  const [tools, setTools] = useState([]);
  const [flows, setFlows] = useState([]);
  const [customParams, setCustomParams] = useState([]);
  const [suggestionType, setSuggestionType] = useState(null); // 'tool' | 'param'
  const [filteredItems, setFilteredItems] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [suggestionCoords, setSuggestionCoords] = useState({ top: 0, left: 0 });
  const [editorInstance, setEditorInstance] = useState(null);

  const mdeRef = useRef(null);

  // Refs for tracking state inside CodeMirror event listeners
  const showSuggestionsRef = useRef(showSuggestions);
  const filteredItemsRef = useRef(filteredItems);
  const suggestionIndexRef = useRef(suggestionIndex);
  const suggestionTypeRef = useRef(suggestionType);

  useEffect(() => {
    showSuggestionsRef.current = showSuggestions;
    filteredItemsRef.current = filteredItems;
    suggestionIndexRef.current = suggestionIndex;
    suggestionTypeRef.current = suggestionType;
  }, [showSuggestions, filteredItems, suggestionIndex, suggestionType]);


  const loadActiveTools = React.useCallback(async () => {
    try {
      const response = await api.getTools();
      const toolsArray = Array.isArray(response) ? response : (response?.tools || []);
      const activeTools = toolsArray.filter(t => t.is_active);
      setTools(activeTools);
    } catch (error) {
      console.error("Error loading tools for instructions:", error);
    }
  }, []);

  const loadFlows = React.useCallback(async () => {
    try {
      const response = await api.getFlows();
      const flowsArray = Array.isArray(response) ? response : (response?.flows || []);
      setFlows(flowsArray);
    } catch (error) {
      console.error("Error loading business flows for instructions:", error);
    }
  }, []);

  const loadCustomParameters = React.useCallback(async () => {
    try {
      if (activeAiAgentId) {
        api.setAiAgentId(activeAiAgentId);
      }
      const response = await api.getWidgetConfig();
      setWidgetConfig(response || null);
      const params = (response && response.custom_parameters) || [];
      setCustomParams(params);
    } catch (error) {
      console.error("Error loading custom parameters/widget config for instructions:", error);
      setWidgetConfig(null);
      setCustomParams([]);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    const checkActiveChannels = async () => {
      try {
        if (activeAiAgentId) {
          api.setAiAgentId(activeAiAgentId);
        }
        const activeChannels = await api.getActiveInstructionChannels();
        if (activeChannels && activeChannels.length === 1) {
          const activeChan = activeChannels[0];
          const exists = CHANNELS.some(c => c.id === activeChan);
          if (exists) {
            setSelectedChannel(activeChan);
          }
        }
      } catch (error) {
        console.error("Error fetching active channels:", error);
      }
    };
    checkActiveChannels();
  }, [activeAiAgentId]);

  useEffect(() => {
    if (activeAiAgentId) {
      api.setAiAgentId(activeAiAgentId);
    }
    loadPrompts();
    loadActiveTools();
    loadFlows();
    loadCustomParameters();
  }, [activeAiAgentId, loadPrompts, loadActiveTools, loadFlows, loadCustomParameters]);

  useEffect(() => {
    if (showSuggestions) {
      if (suggestionType === "tool") {
        const q = suggestionQuery.toLowerCase();
        const allAtItems = [
          ...SPECIAL_AT_ITEMS,
          ...flows.map(f => ({ id: `flow_${f.id}`, name: f.name, label: f.name, description: f.description || 'Business flow', type: 'flow' })),
          ...tools.map(t => ({ ...t, type: 'tool' }))
        ];
        const filtered = allAtItems.filter(item =>
          item.name.toLowerCase().includes(q) ||
          (item.label || '').toLowerCase().includes(q)
        );
        setFilteredItems(filtered);
        setSuggestionIndex((prevIndex) => (prevIndex >= filtered.length ? 0 : prevIndex));
      } else if (suggestionType === "param") {
        const inbuiltParams = ["name", "email", "phone", "userid"];
        const allParams = Array.from(new Set([...inbuiltParams, ...customParams]));
        const filtered = allParams.filter(param =>
          param.toLowerCase().includes(suggestionQuery.toLowerCase())
        );
        setFilteredItems(filtered);
        setSuggestionIndex((prevIndex) => (prevIndex >= filtered.length ? 0 : prevIndex));
      }
    } else {
      setFilteredItems([]);
    }
  }, [suggestionQuery, showSuggestions, tools, flows, customParams, suggestionType, setFilteredItems, setSuggestionIndex]);

  // Recalculate suggestions box coordinates dynamically based on actual filtered item count height and scroll events
  useEffect(() => {
    if (!showSuggestions || !editorInstance || filteredItems.length === 0) return;

    const updateCoords = () => {
      const cm = editorInstance.codemirror;
      const coords = cm.cursorCoords(true, "window");
      const estimatedHeight = Math.min(filteredItemsRef.current.length * 45 + 10, 240);
      const spaceBelow = window.innerHeight - coords.bottom;
      const showAbove = spaceBelow < estimatedHeight && coords.top > estimatedHeight;

      setSuggestionCoords({
        top: showAbove ? coords.top - estimatedHeight - 5 : coords.bottom + 5,
        left: coords.left
      });
    };

    updateCoords();

    // Listen to scroll events on window or any scrollable container (capturing phase)
    window.addEventListener("scroll", updateCoords, true);
    return () => window.removeEventListener("scroll", updateCoords, true);
  }, [filteredItems, showSuggestions, editorInstance]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.EasyMDEContainer') && !e.target.closest('.suggestion-box')) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleCreatePrompt = async () => {
    if (!currentPrompt.trim()) return;
    if (prompts.length >= 5) {
      showToast("You have reached the limit of 5 prompts. Please delete an older version to add a new one.", "warning");
      return;
    }

    setSaving(true);
    try {
      const name = versionName.trim();
      await api.createInstructions(
        currentPrompt,
        name,
        selectedChannel,
        guardrailsEnabled,
        guardrailsContent,
        afterHoursEnabled,
        afterHoursInstruction
      );
      setVersionName("");
      await loadPrompts();
      setActiveTab("history");
      showToast("New prompt version created and activated!", "success");
    } catch (error) {
      showToast(error.message || "Failed to create prompt version", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    if (currentActive) return; // Already is_active

    try {
      await api.activateInstruction(id);
      await loadPrompts();
      showToast("Instruction activated successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to activate prompt", "error");
    }
  };

  const handleDelete = (id, isActive) => {
    if (isActive) {
      showToast("Cannot delete the currently active prompt. Please activate another one first.", "error");
      return;
    }
    setPromptToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!promptToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteInstruction(promptToDelete);
      await loadPrompts();
      setIsDeleteModalOpen(false);
      showToast("Prompt version deleted successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to delete prompt", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImprove = async () => {
    const textToImprove = instructionMode === "after_hours" ? afterHoursInstruction : currentPrompt;
    if (!textToImprove.trim()) return;
    setImproving(true);
    try {
      const response = await api.improvePrompt(textToImprove);
      if (response.success) {
        if (instructionMode === "after_hours") {
          setAfterHoursInstruction(response.improved_prompt);
        } else {
          setCurrentPrompt(response.improved_prompt);
        }
      }
    } catch (error) {
      showToast("Failed to improve prompt", "error");
    } finally {
      setImproving(false);
    }
  };

  const handleEditorActivity = (cm) => {
    const doc = cm.getDoc();
    const cursor = doc.getCursor();
    const lineText = doc.getLine(cursor.line) || "";
    const textBeforeCursor = lineText.substring(0, cursor.ch);

    const lastAt = textBeforeCursor.lastIndexOf("@");
    const lastParam = textBeforeCursor.lastIndexOf("{{");

    if (lastAt !== -1 && lastAt > lastParam && (lastAt === 0 || /\s/.test(textBeforeCursor.charAt(lastAt - 1)))) {
      const query = textBeforeCursor.substring(lastAt + 1);
      if (!/\s/.test(query)) {
        setSuggestionQuery(query);
        setSuggestionType("tool");
        setShowSuggestions(true);

        const coords = cm.cursorCoords(true, "window");
        setSuggestionCoords({
          top: coords.bottom + 5,
          left: coords.left
        });
        return;
      }
    } else if (lastParam !== -1 && lastParam > lastAt && (lastParam === 0 || /\s/.test(textBeforeCursor.charAt(lastParam - 1)))) {
      const query = textBeforeCursor.substring(lastParam + 2);
      if (!/\s/.test(query) && !query.includes("}")) {
        setSuggestionQuery(query);
        setSuggestionType("param");
        setShowSuggestions(true);

        const coords = cm.cursorCoords(true, "window");
        setSuggestionCoords({
          top: coords.bottom + 5,
          left: coords.left
        });
        return;
      }
    }

    setShowSuggestions(false);
    setSuggestionType(null);
  };

  const handleEditorKeydown = (cm, e) => {
    if (!showSuggestionsRef.current || filteredItemsRef.current.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestionIndex((prev) => (prev + 1) % filteredItemsRef.current.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestionIndex((prev) => (prev - 1 + filteredItemsRef.current.length) % filteredItemsRef.current.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      selectItem(filteredItemsRef.current[suggestionIndexRef.current]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  const selectItem = (item) => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const doc = cm.getDoc();
    const cursor = doc.getCursor();
    const lineText = doc.getLine(cursor.line) || "";
    const textBeforeCursor = lineText.substring(0, cursor.ch);

    if (suggestionTypeRef.current === "tool") {
      const atIndex = textBeforeCursor.lastIndexOf("@");
      if (atIndex !== -1) {
        const from = { line: cursor.line, ch: atIndex };
        const to = { line: cursor.line, ch: cursor.ch };
        let completedText;
        if (item.type === 'flow') {
          completedText = `**business_flow_loader(flow_name='${item.name}')** `;
        } else if (item.name === 'request_human_assistant') {
          completedText = `**request_human_assistant(reason='User requested human assistance')** `;
        } else {
          completedText = `**${item.name}** `;
        }
        doc.replaceRange(completedText, from, to);
        setShowSuggestions(false);
        setTimeout(() => {
          cm.focus();
          // Force update markings after insertion
          if (typeof triggerMarkUpdate === 'function') {
            triggerMarkUpdate();
          }
        }, 0);
      }
    } else if (suggestionTypeRef.current === "param") {
      const lastBraceIndex = textBeforeCursor.lastIndexOf("{{");
      if (lastBraceIndex !== -1) {
        const from = { line: cursor.line, ch: lastBraceIndex };
        const to = { line: cursor.line, ch: cursor.ch };
        const completedText = `{{${item}}} `;
        doc.replaceRange(completedText, from, to);
        setShowSuggestions(false);
        setTimeout(() => cm.focus(), 0);
      }
    }
  };

  // Bind events to CodeMirror instance
  const activityHandlerRef = useRef();
  const keydownHandlerRef = useRef();

  activityHandlerRef.current = () => {
    if (editorInstance) {
      handleEditorActivity(editorInstance.codemirror);
    }
  };
  keydownHandlerRef.current = (cm, e) => {
    handleEditorKeydown(cm, e);
  };

  // Tooltip hover states and markings logic
  const [hoveredFlow, setHoveredFlow] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const markedRangesRef = useRef([]);

  const flowsRef = useRef(flows);
  useEffect(() => {
    flowsRef.current = flows;
  }, [flows]);

  const triggerMarkUpdate = React.useCallback(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const doc = cm.getDoc();

    // Clear previous marks
    markedRangesRef.current.forEach(mark => mark.clear());
    markedRangesRef.current = [];

    const totalLines = doc.lineCount();
    for (let i = 0; i < totalLines; i++) {
      const text = doc.getLine(i);

      // 1. Scan for **business_flow_loader(flow_name='FlowName')**
      const flowRegex = /\*\*business_flow_loader\(flow_name='([^']+)'\)\*\*/g;
      let match;
      while ((match = flowRegex.exec(text)) !== null) {
        const startCh = match.index;
        const endCh = match.index + match[0].length;
        const flowName = match[1];

        const matchedFlow = flowsRef.current.find(f => f.name.toLowerCase() === flowName.toLowerCase());
        const keywords = matchedFlow ? (matchedFlow.keywords || matchedFlow.parameters || []) : [];
        const description = matchedFlow ? (matchedFlow.description || "") : "";
        const keywordsStr = Array.isArray(keywords) ? keywords.join(", ") : String(keywords);

        // Build widget showing only the function name
        const widget = document.createElement("span");
        widget.className = "flow-chip-cm";
        widget.textContent = "business_flow_loader";
        widget.setAttribute("data-flowname", flowName);
        widget.setAttribute("data-type", "flow");
        widget.setAttribute("data-keywords", keywordsStr);
        widget.setAttribute("data-desc", description);

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 2. Scan for **request_human_assistant(reason='...')**
      const handoffRegex = /\*\*request_human_assistant\(([^)]*)\)\*\*/g;
      let handoffMatch;
      while ((handoffMatch = handoffRegex.exec(text)) !== null) {
        const startCh = handoffMatch.index;
        const endCh = handoffMatch.index + handoffMatch[0].length;
        const rawArgs = handoffMatch[1]; // e.g. reason='User requested human assistance'

        // Extract actual reason value if present
        const reasonMatch = rawArgs.match(/reason=['"]([^'"]+)['"]/);
        const reasonValue = reasonMatch ? reasonMatch[1] : null;
        const paramsDisplay = reasonValue
          ? `reason='${reasonValue}'`
          : (rawArgs.trim() || "reason (string)");

        const widget = document.createElement("span");
        widget.className = "tool-chip-cm";
        widget.textContent = "request_human_assistant";
        widget.setAttribute("data-toolname", "request_human_assistant");
        widget.setAttribute("data-type", "tool");
        widget.setAttribute("data-params", paramsDisplay);
        widget.setAttribute("data-desc", "Escalate conversation to a live human agent");

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 3. Scan for other API tool chips: **ToolName** (simple bold, no parens)
      const toolRegex = /\*\*([a-zA-Z0-9_]+)\*\*/g;
      let toolMatch;
      while ((toolMatch = toolRegex.exec(text)) !== null) {
        const startCh = toolMatch.index;
        const endCh = toolMatch.index + toolMatch[0].length;
        const toolName = toolMatch[1];

        // Skip business_flow_loader and request_human_assistant (handled above)
        if (toolName === "business_flow_loader" || toolName === "request_human_assistant") continue;

        const matchedTool = tools.find(t => t.name.toLowerCase() === toolName.toLowerCase());
        if (!matchedTool) continue;

        const description = matchedTool.description || "";
        const parameters = matchedTool.parameters || matchedTool.properties || "No parameters";

        const widget = document.createElement("span");
        widget.className = "tool-chip-cm";
        widget.textContent = toolName;
        widget.setAttribute("data-toolname", toolName);
        widget.setAttribute("data-type", "tool");
        widget.setAttribute("data-params", typeof parameters === "object" ? JSON.stringify(parameters) : String(parameters));
        widget.setAttribute("data-desc", description);

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }

      // 4. Scan for {{variable}} patterns
      const varRegex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
      let varMatch;
      while ((varMatch = varRegex.exec(text)) !== null) {
        const startCh = varMatch.index;
        const endCh = varMatch.index + varMatch[0].length;
        const varName = varMatch[1];

        const widget = document.createElement("span");
        widget.className = "var-chip-cm";
        widget.textContent = varName;
        widget.setAttribute("data-varname", varName);
        widget.setAttribute("data-type", "variable");

        const mark = cm.markText(
          { line: i, ch: startCh },
          { line: i, ch: endCh },
          { replacedWith: widget, handleMouseEvents: true }
        );
        markedRangesRef.current.push(mark);
      }
    }
  }, [editorInstance, tools]);

  // Handle hover tooltips on flow and tool chips
  useEffect(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;
    const wrapper = cm.getWrapperElement();

    const handleMouseOver = (e) => {
      const flowTarget = e.target.closest(".flow-chip-cm");
      const toolTarget = e.target.closest(".tool-chip-cm");
      const varTarget = e.target.closest(".var-chip-cm");

      if (flowTarget) {
        const flowName = flowTarget.getAttribute("data-flowname");
        const keywords = flowTarget.getAttribute("data-keywords");
        const desc = flowTarget.getAttribute("data-desc");
        const rect = flowTarget.getBoundingClientRect();
        setHoveredFlow({
          name: flowName,
          type: "flow",
          keywords: keywords || "No parameters specified",
          description: desc
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else if (toolTarget) {
        const toolName = toolTarget.getAttribute("data-toolname");
        const params = toolTarget.getAttribute("data-params");
        const desc = toolTarget.getAttribute("data-desc");
        const rect = toolTarget.getBoundingClientRect();

        let displayParams = params;
        try {
          const parsed = JSON.parse(params);
          if (typeof parsed === "object") {
            displayParams = Object.keys(parsed).join(", ");
          }
        } catch (err) { }

        setHoveredFlow({
          name: toolName,
          type: "tool",
          keywords: displayParams || "No parameters specified",
          description: desc
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else if (varTarget) {
        const varName = varTarget.getAttribute("data-varname");
        const rect = varTarget.getBoundingClientRect();
        setHoveredFlow({
          name: varName,
          type: "variable",
          keywords: `{{${varName}}}`,
          description: `Dynamic variable — will be replaced with the user's ${varName} value at runtime.`
        });
        setTooltipPos({
          top: rect.bottom + 6,
          left: rect.left
        });
      } else {
        setHoveredFlow(null);
      }
    };

    const handleMouseLeave = (e) => {
      if (!e.relatedTarget || (!e.relatedTarget.closest(".flow-chip-cm") && !e.relatedTarget.closest(".tool-chip-cm") && !e.relatedTarget.closest(".var-chip-cm"))) {
        setHoveredFlow(null);
      }
    };

    wrapper.addEventListener("mouseover", handleMouseOver);
    wrapper.addEventListener("mouseout", handleMouseLeave);

    // Update marks initially and on prompt changes
    triggerMarkUpdate();
    const changeListener = () => {
      setTimeout(triggerMarkUpdate, 10);
    };
    cm.on("change", changeListener);

    return () => {
      wrapper.removeEventListener("mouseover", handleMouseOver);
      wrapper.removeEventListener("mouseout", handleMouseLeave);
      cm.off("change", changeListener);
      markedRangesRef.current.forEach(mark => mark.clear());
    };
  }, [editorInstance, flows, tools, triggerMarkUpdate]);

  useEffect(() => {
    if (!editorInstance) return;
    const cm = editorInstance.codemirror;

    const activityListener = () => activityHandlerRef.current();
    const keydownListener = (cmInst, e) => keydownHandlerRef.current(cmInst, e);

    cm.on("cursorActivity", activityListener);
    cm.on("keydown", keydownListener);

    return () => {
      cm.off("cursorActivity", activityListener);
      cm.off("keydown", keydownListener);
    };
  }, [editorInstance]);

  useEffect(() => {
    if (activeTab === "current" && editorInstance?.codemirror) {
      const timer = setTimeout(() => {
        editorInstance.codemirror.refresh();
      }, 310);
      return () => clearTimeout(timer);
    }
  }, [activeTab, editorInstance]);

  const selectedChannelObj = CHANNELS.find(c => c.id === selectedChannel) || CHANNELS[0];

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-base sm:text-xl font-bold text-zinc-900 tracking-tight">Agent Instructions</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Define operational behavior, system instructions, guardrails, and dynamic parameters for each channel.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200/80 rounded-lg p-1">
              <span className="text-[11px] font-semibold text-zinc-500 pl-2 shrink-0">Channel:</span>
              <CustomDropdown
                options={CHANNELS}
                value={selectedChannel}
                onChange={setSelectedChannel}
                className="min-w-[150px] sm:min-w-[170px]"
              />
            </div>
          </div>
        </div>

        {/* Tab Switcher with sliding pill */}
        <div className="pt-3 pb-4 flex justify-center">
          <div className="relative flex items-center p-1 bg-zinc-100 rounded-lg w-full max-w-[340px] border border-zinc-200/50">
            {/* Sliding Pill Indicator */}
            <span
              className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-white rounded-md shadow-2xs border border-zinc-200/60 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] pointer-events-none ${
                activeTab === "current" ? "translate-x-0" : "translate-x-full"
              }`}
            />

            <button
              type="button"
              onClick={() => setActiveTab("current")}
              className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                activeTab === "current"
                  ? "text-zinc-900"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <Zap size={13} />
              <span>Prompt Editor</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                activeTab === "history"
                  ? "text-zinc-900"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <History size={13} />
              <span>Version History</span>
              <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold transition-colors duration-200 ${
                activeTab === "history" ? "bg-zinc-100 text-zinc-800" : "bg-zinc-200/70 text-zinc-600"
              }`}>
                {prompts.length}/5
              </span>
            </button>
          </div>
        </div>

        {/* Sliding Tab Track */}
        <div className="overflow-hidden w-full pt-1">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 2}%)`,
              width: "200%",
            }}
          >
            {/* Panel 1: Prompt Editor */}
            <div className="w-1/2 shrink-0 sm:px-1">
              <div className="w-full space-y-3 sm:space-y-4">
            {/* Editor Card */}
            <div className={`bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-3 sm:p-5 flex flex-col justify-between transition-all duration-200 ${
              isFullScreen ? "fixed inset-2 sm:inset-4 z-[9999] p-4 sm:p-6 shadow-2xl overflow-y-auto" : "relative"
            }`}>

              {/* Header with Switcher and AI Enhancer */}
              <div className="flex items-start sm:items-center justify-between pb-3 mb-3 border-b border-zinc-100 flex-col sm:flex-row gap-2.5 sm:gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xs sm:text-sm font-semibold text-zinc-900">
                      {instructionMode === "after_hours" ? "AI After-Hours Instructions" : "System Instruction Prompt"}
                    </h3>

                    {/* Mode Switcher - only appears when channel is Chat Widget AND after_hours_support is enabled */}
                    {(selectedChannel === "widget" || selectedChannel === "webchat") && widgetConfig?.after_hours_enabled && (
                      <div className="inline-flex items-center p-0.5 bg-zinc-100 rounded-lg border border-zinc-200/80 ml-1">
                        <button
                          type="button"
                          onClick={() => setInstructionMode("normal")}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                            instructionMode === "normal"
                              ? "bg-white text-zinc-900 shadow-2xs border border-zinc-200/60"
                              : "text-zinc-500 hover:text-zinc-900"
                          }`}
                        >
                          <Zap size={11} className={instructionMode === "normal" ? "text-zinc-900" : "text-zinc-400"} />
                          <span>Standard Hours</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setInstructionMode("after_hours")}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                            instructionMode === "after_hours"
                              ? "bg-zinc-900 text-white shadow-2xs"
                              : "text-zinc-500 hover:text-zinc-900"
                          }`}
                        >
                          <Moon size={11} className={instructionMode === "after_hours" ? "text-amber-300" : "text-zinc-400"} />
                          <span>After-Hours</span>
                          {afterHoursInstruction.trim() && (
                            <span className={`w-1.5 h-1.5 rounded-full ${instructionMode === "after_hours" ? "bg-amber-400" : "bg-emerald-500"}`}></span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-[11px] sm:text-xs text-zinc-500 mt-1">
                    {instructionMode === "after_hours" ? (
                      <span>
                        Instructions executed dynamically outside business hours. If left empty, the agent falls back to standard instructions.
                      </span>
                    ) : (
                      <span>
                        Type <span className="font-mono font-semibold text-zinc-700">@</span> to insert Tools / Business Flows or <span className="font-mono font-semibold text-zinc-700">{"{{"}</span> for custom parameters.
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
                  {instructionMode === "after_hours" && (
                    <span className="text-[10px] font-mono text-zinc-400 px-2 py-1 bg-zinc-50 rounded border border-zinc-200/60">
                      {afterHoursInstruction.length} chars
                    </span>
                  )}
                  <button
                    onClick={handleImprove}
                    disabled={improving || (instructionMode === "after_hours" ? !afterHoursInstruction.trim() : !currentPrompt.trim())}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/70 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer shrink-0"
                  >
                    <Sparkles size={13} className={improving ? "animate-spin" : ""} />
                    <span>{improving ? "Refining with AI..." : "Enhance with AI"}</span>
                  </button>
                </div>
              </div>

              {/* Markdown rich-text editor */}
              <div className="relative text-left">
                <style>{`
                  .EasyMDEContainer {
                    width: 100%;
                  }
                  .editor-toolbar {
                    border: 1px solid #e4e4e7 !important;
                    border-top-left-radius: 8px !important;
                    border-top-right-radius: 8px !important;
                    background-color: #fafafa !important;
                    padding: 4px 8px !important;
                    overflow-x: auto !important;
                    white-space: nowrap !important;
                  }
                  .editor-toolbar button {
                    border-radius: 6px !important;
                    transition: all 0.15s !important;
                  }
                  .editor-toolbar button.active, .editor-toolbar button:hover {
                    background-color: #e4e4e7 !important;
                  }
                  .CodeMirror {
                    border: 1px solid #e4e4e7 !important;
                    border-top: none !important;
                    border-bottom-left-radius: 8px !important;
                    border-bottom-right-radius: 8px !important;
                    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                    font-size: 13px !important;
                    line-height: 1.6 !important;
                    padding: 8px 10px !important;
                    min-height: 280px !important;
                    background-color: #ffffff !important;
                    color: #18181b !important;
                  }
                  @media (min-width: 640px) {
                    .editor-toolbar {
                      padding: 6px 12px !important;
                    }
                    .CodeMirror {
                      padding: 10px 12px !important;
                      min-height: 380px !important;
                    }
                  }
                  .CodeMirror .cm-header-1 {
                    font-size: 1.4rem !important;
                    line-height: 1.35 !important;
                    font-weight: 700 !important;
                  }
                  .CodeMirror .cm-header-2 {
                    font-size: 1.2rem !important;
                    line-height: 1.35 !important;
                    font-weight: 600 !important;
                  }
                  .CodeMirror .cm-header-3 {
                    font-size: 1.05rem !important;
                    line-height: 1.35 !important;
                    font-weight: 600 !important;
                  }
                  .editor-preview-active-side {
                    border-bottom-left-radius: 8px !important;
                    border-bottom-right-radius: 8px !important;
                  }
                  .flow-chip-cm {
                    background: #f5f3ff !important;
                    color: #7c3aed !important;
                    border: 1px solid #ddd6fe !important;
                    border-radius: 5px !important;
                    padding: 1px 6px !important;
                    font-weight: 600 !important;
                    font-size: 11px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 18px !important;
                    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.08) !important;
                    margin: 0 2px !important;
                    font-family: Inter, sans-serif !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .flow-chip-cm::before {
                    content: '⚡ ' !important;
                    font-size: 10px !important;
                    margin-right: 2px !important;
                    opacity: 0.8 !important;
                  }
                  .flow-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                  .tool-chip-cm {
                    background: #eff6ff !important;
                    color: #2563eb !important;
                    border: 1px solid #bfdbfe !important;
                    border-radius: 5px !important;
                    padding: 1px 6px !important;
                    font-weight: 600 !important;
                    font-size: 11px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 18px !important;
                    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.08) !important;
                    margin: 0 2px !important;
                    font-family: Inter, sans-serif !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .tool-chip-cm::before {
                    content: '🔧 ' !important;
                    font-size: 10px !important;
                    margin-right: 2px !important;
                    opacity: 0.8 !important;
                  }
                  .tool-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                  .var-chip-cm {
                    background: #ecfdf5 !important;
                    color: #059669 !important;
                    border: 1px solid #6ee7b7 !important;
                    border-radius: 5px !important;
                    padding: 1px 6px !important;
                    font-weight: 600 !important;
                    font-size: 11px !important;
                    cursor: help !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    vertical-align: middle !important;
                    line-height: 18px !important;
                    box-shadow: 0 1px 2px rgba(5, 150, 105, 0.08) !important;
                    margin: 0 2px !important;
                    font-family: monospace !important;
                    user-select: none !important;
                    transition: filter 0.15s !important;
                  }
                  .var-chip-cm::before {
                    content: '{{ ' !important;
                    font-size: 10px !important;
                    opacity: 0.6 !important;
                    font-family: monospace !important;
                  }
                  .var-chip-cm::after {
                    content: ' }}' !important;
                    font-size: 10px !important;
                    opacity: 0.6 !important;
                    font-family: monospace !important;
                  }
                  .var-chip-cm:hover {
                    filter: brightness(0.92) !important;
                  }
                `}</style>
                <SimpleMdeEditor
                  value={instructionMode === "after_hours" ? afterHoursInstruction : currentPrompt}
                  onChange={(val) => {
                    if (instructionMode === "after_hours") {
                      setAfterHoursInstruction(val);
                      if (val.trim()) {
                        setAfterHoursEnabled(true);
                      }
                    } else {
                      setCurrentPrompt(val);
                    }
                  }}
                  options={mdeOptions}
                  getMdeInstance={(instance) => {
                    mdeRef.current = instance;
                    setEditorInstance(instance);
                  }}
                />

                {/* Floating parameters tooltip */}
                {hoveredFlow && ReactDOM.createPortal(
                  <div
                    className="fixed z-[10001] bg-zinc-950 text-white rounded-xl p-3 shadow-xl max-w-xs text-xs space-y-1.5 animate-fadeIn border border-zinc-800"
                    style={{
                      top: `${tooltipPos.top}px`,
                      left: `${tooltipPos.left}px`,
                    }}
                  >
                    <div className={`font-semibold flex items-center gap-1.5 ${
                      hoveredFlow.type === "flow" ? "text-violet-400" : hoveredFlow.type === "variable" ? "text-emerald-400" : "text-blue-400"
                    }`}>
                      {hoveredFlow.type === "flow" ? (
                        <Zap size={12} className="fill-violet-400" />
                      ) : hoveredFlow.type === "variable" ? null : (
                        <Wrench size={12} className="text-blue-400" />
                      )}
                      <span className="font-mono text-[11px]">
                        {hoveredFlow.type === "flow"
                          ? `business_flow_loader(flow_name='${hoveredFlow.name}')`
                          : hoveredFlow.type === "variable"
                            ? `{{${hoveredFlow.name}}}`
                            : hoveredFlow.keywords && hoveredFlow.keywords !== "No parameters specified"
                              ? `${hoveredFlow.name}(${hoveredFlow.keywords})`
                              : hoveredFlow.name
                        }
                      </span>
                    </div>
                    {hoveredFlow.description && (
                      <p className="text-[11px] text-zinc-400 leading-normal">{hoveredFlow.description}</p>
                    )}
                    <div className="pt-1.5 border-t border-zinc-800/80 flex flex-col gap-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">
                        {hoveredFlow.type === "flow" ? "Parameters / Keywords:" : hoveredFlow.type === "variable" ? "Variable Syntax:" : "Parameters / Schema:"}
                      </span>
                      <span className="text-zinc-200 font-mono text-[10px] leading-relaxed bg-zinc-900 px-1.5 py-0.5 rounded border border-zinc-800 w-fit max-w-full truncate">
                        {hoveredFlow.keywords}
                      </span>
                    </div>
                  </div>,
                  document.body
                )}

                {/* Auto-complete popup */}
                {showSuggestions && filteredItems.length > 0 && ReactDOM.createPortal(
                  <div
                    className="suggestion-box fixed z-[10000] bg-white border border-zinc-200 rounded-xl shadow-xl max-h-60 overflow-y-auto w-64 divide-y divide-zinc-100"
                    style={{
                      top: `${suggestionCoords.top}px`,
                      left: `${suggestionCoords.left}px`,
                    }}
                  >
                    <div className="p-1 space-y-0.5">
                      {filteredItems.map((item, idx) => (
                        <button
                          key={suggestionType === "tool" ? item.id : item}
                          onClick={() => selectItem(item)}
                          className={`w-full text-left px-2.5 py-2 text-xs flex flex-col gap-0.5 transition-colors rounded-lg ${
                            idx === suggestionIndex
                              ? "bg-zinc-900 text-white font-medium shadow-xs"
                              : "text-zinc-700 hover:bg-zinc-100"
                          }`}
                        >
                          {suggestionType === "tool" ? (
                            <>
                              <div className="flex items-center gap-1.5 font-medium">
                                {item.type === 'special' && (
                                  <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                                    idx === suggestionIndex ? 'bg-rose-900 text-rose-200' : 'bg-rose-50 text-rose-600 border border-rose-200/60'
                                  }`}>HUMAN</span>
                                )}
                                {item.type === 'flow' && (
                                  <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                                    idx === suggestionIndex ? 'bg-violet-900 text-violet-200' : 'bg-violet-50 text-violet-600 border border-violet-200/60'
                                  }`}>FLOW</span>
                                )}
                                {item.type === 'tool' && (
                                  <span className={idx === suggestionIndex ? 'text-blue-300 font-bold' : 'text-blue-600 font-bold'}>@</span>
                                )}
                                <span>{item.label || item.name}</span>
                              </div>
                              {item.description && (
                                <div className={`text-[10px] truncate max-w-full ${
                                  idx === suggestionIndex ? 'text-zinc-300' : 'text-zinc-400'
                                }`}>
                                  {item.description}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-1.5 font-medium">
                              <span className={idx === suggestionIndex ? "text-indigo-300" : "text-indigo-600 font-semibold"}>{"{{"}</span>
                              <span>{item}</span>
                              <span className={idx === suggestionIndex ? "text-indigo-300" : "text-indigo-600 font-semibold"}>{"}}"}</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              {/* Version Name Input & Save Bar */}
              <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
                <div className="w-full sm:flex-1">
                  <input
                    type="text"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    placeholder="Version name (e.g. V2 - Friendly & Structured)"
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400"
                  />
                </div>
                <button
                  onClick={handleCreatePrompt}
                  disabled={saving || !currentPrompt.trim() || !versionName.trim()}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <Save size={14} />
                  <span>{saving ? "Saving..." : "Save & Activate"}</span>
                </button>
              </div>
            </div>

            {/* Guardrails Configuration Card */}
            <div className="bg-white rounded-xl p-3 sm:p-5 border border-zinc-200/80 shadow-2xs">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                  <Shield className="text-zinc-800 shrink-0" size={18} />
                  <div className="min-w-0">
                    <h3 className="text-xs sm:text-sm font-semibold text-zinc-900 truncate">Custom Guardrails</h3>
                    <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5 line-clamp-1 sm:line-clamp-none">Enforce strict behavioral boundaries and safety rules.</p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={guardrailsEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setGuardrailsEnabled(checked);
                      handleSaveGuardrails(checked);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-zinc-900"></div>
                </label>
              </div>

              {guardrailsEnabled && (
                <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
                  <textarea
                    rows={4}
                    value={guardrailsContent}
                    onChange={(e) => setGuardrailsContent(e.target.value)}
                    placeholder="Define guidelines... (e.g. Do not talk about competitors, Do not provide medical advice, Always refer pricing questions to sales team)"
                    className="w-full px-3.5 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 text-xs transition-colors"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleSaveGuardrails()}
                      disabled={savingGuardrails}
                      className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] disabled:opacity-40 cursor-pointer"
                    >
                      <Save size={13} />
                      <span>{savingGuardrails ? "Saving..." : "Save Guardrails"}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Panel 2: Version History */}
        <div className="w-1/2 shrink-0 sm:px-1">
          <div className="w-full space-y-4">
                {/* Version History Table */}
            <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs overflow-hidden">
              <div className="px-4 py-3 sm:px-5 border-b border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900">Saved Versions</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Manage and activate previous instruction revisions for {selectedChannelObj.name}.</p>
                </div>
                <span className="text-xs font-semibold text-zinc-600 bg-white border border-zinc-200 px-2.5 py-1 rounded-md shadow-2xs">
                  {prompts.length} / 5 versions
                </span>
              </div>

              {prompts.length === 0 ? (
                <div className="p-12 text-center text-zinc-400">
                  <History size={40} className="mx-auto mb-3 opacity-30 text-zinc-400" />
                  <p className="text-xs font-medium text-zinc-600">No prompt versions saved yet</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Use the Prompt Editor to create and save instructions.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-200/80 bg-zinc-50/50 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                          <th className="py-2.5 px-4 font-semibold">Version Name</th>
                          <th className="py-2.5 px-4 font-semibold">Content Preview</th>
                          <th className="py-2.5 px-4 font-semibold">Last Updated</th>
                          <th className="py-2.5 px-4 font-semibold text-center">Status</th>
                          <th className="py-2.5 px-4 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 text-xs">
                        {prompts.map((p) => (
                          <tr key={p.id} className={`transition-colors hover:bg-zinc-50/60 ${p.is_active ? 'bg-zinc-50/30' : ''}`}>
                            <td className="py-3 px-4 font-semibold text-zinc-900">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[180px]">{p.version_name}</span>
                                {p.is_active && (
                                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-1.5 py-0.2 rounded text-[10px] font-semibold">
                                    <CheckCircle2 size={10} /> Active
                                  </span>
                                )}
                                {p.after_hours_enabled && (
                                  <span className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 border border-zinc-200/80 px-1.5 py-0.2 rounded text-[10px] font-semibold" title="Includes after-hours instructions">
                                    <Moon size={9} /> After-Hours
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-zinc-500 font-mono text-[11px] max-w-[320px] truncate">
                              {p.content}
                            </td>
                            <td className="py-3 px-4 text-zinc-500 text-[11px]">
                              {formatDate(p.updated_at || p.created_at)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => handleToggleActive(p.id, p.is_active)}
                                disabled={p.is_active}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                                  p.is_active
                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60 cursor-default"
                                    : "bg-zinc-100 hover:bg-zinc-200 text-zinc-700 cursor-pointer"
                                }`}
                              >
                                {p.is_active ? "Current Active" : "Activate"}
                              </button>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {p.is_active ? (
                                <span
                                  className="inline-flex items-center justify-center p-1.5 text-zinc-400 cursor-help"
                                  title="Active Prompt — protected from deletion"
                                >
                                  <Shield size={15} />
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleDelete(p.id, p.is_active)}
                                  className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                                  title="Delete Version"
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden divide-y divide-zinc-100">
                    {prompts.map((p) => (
                      <div key={p.id} className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-zinc-900 text-xs">{p.version_name}</span>
                            {p.is_active && (
                              <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-1.5 py-0.2 rounded text-[10px] font-semibold">
                                Active
                              </span>
                            )}
                          </div>
                          {!p.is_active && (
                            <button
                              onClick={() => handleDelete(p.id, p.is_active)}
                              className="text-zinc-400 hover:text-red-600 p-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 font-mono truncate">{p.content}</p>
                        <div className="flex items-center justify-between pt-1">
                          <span className="text-[10px] text-zinc-400">
                            {formatDate(p.updated_at || p.created_at)}
                          </span>
                          {!p.is_active && (
                            <button
                              onClick={() => handleToggleActive(p.id, p.is_active)}
                              className="px-2.5 py-1 bg-zinc-900 text-white rounded text-[11px] font-semibold"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <p className="text-center text-xs text-zinc-400 pt-1">
              Only the active prompt governs the AI agent's responses for this channel. Max 5 versions retained.
            </p>
          </div>
        </div>
      </div>
    </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Instruction Version?"
        message="This action will permanently delete this prompt revision from history. Are you sure you want to proceed?"
      />
    </div>
  );
};

export default PromptVersioning;
